import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  PlusOutlined, 
  MinusOutlined, 
  ApartmentOutlined,
  BranchesOutlined,
  NodeIndexOutlined,
  WarningOutlined
} from "@ant-design/icons";
import { Button, Progress } from "antd";
import PlanInfoModal from "../modal/PlanInfoModal";
import GetTopPlansApi from "../../api/GetTopPlansApi";
import GetCascadedPlansApi from "../../api/plans/GetCascadedPlansApi";
import GetSplitPlanApi from "../../api/split-plans/GetSplitPlanApi";
import PageLoader from "../sub/PageLoader";
import "./FullTreeView.css";

const FullTreeViewMock = () => {
  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState(null);
  const [planModal, setPlanModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [childrenCache, setChildrenCache] = useState(new Map());
  const [splitChildrenCache, setSplitChildrenCache] = useState(new Map());

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id) {
      fetchTopPlansData();
    }
  }, [perfCycleState]);

  const fetchTopPlansData = async () => {
    setLoading(true);
    try {
      const apiResponse = await GetTopPlansApi(
        perfCycleState.id,
        positionState.id,
        0,
        "ASC",
        100
      );
      
      if (apiResponse.success && apiResponse.data?.plans) {
        const transformedPlans = apiResponse.data.plans.map(plan => ({
          id: plan.planId,
          name: plan.title,
          planGroup: plan.planGroup?.name || plan.planGroup,
          level: plan.unit?.name || "Organization",
          progress: plan.target && plan.achievedTarget ? 
            Math.round((plan.achievedTarget / plan.target) * 100) : 0,
          target: plan.target,
          achievedTarget: plan.achievedTarget,
          canUpdate: plan.canUpdate,
          hasChildren: plan.canUpdate === false,
          hasSplitPlans: false,
          assignedTo: plan.assignedTo,
          progressStatus: plan.progressStatus,
          scale: plan.scale,
          scaleUnit: plan.scaleUnit
        }));
        
        setTreeData({ plans: transformedPlans });
        checkForSplitPlans(transformedPlans);
      }
    } catch (error) {
      console.error("Error fetching top plans:", error);
    }
    setLoading(false);
  };

  const checkForSplitPlans = async (plans) => {
    const updatedPlans = [...plans];
    let hasUpdates = false;

    for (let i = 0; i < updatedPlans.length; i++) {
      try {
        const apiResponse = await GetSplitPlanApi(updatedPlans[i].id, 0, 1);
        if (apiResponse.success && apiResponse.data?.totalCount > 0) {
          updatedPlans[i].hasSplitPlans = true;
          hasUpdates = true;
        }
      } catch (error) {
        console.error(`Error checking split plans for ${updatedPlans[i].id}:`, error);
      }
    }

    if (hasUpdates) {
      setTreeData({ plans: updatedPlans });
    }
  };

  const fetchCascadedPlans = async (planId) => {
    try {
      const apiResponse = await GetCascadedPlansApi(
        perfCycleState.id,
        planId,
        positionState.id,
        0,
        50
      );
      
      if (apiResponse.message === "success" && apiResponse.data?.plans) {
        const transformedChildren = apiResponse.data.plans.map(plan => ({
          id: plan.planId,
          name: plan.title,
          planGroup: plan.planGroup?.name || plan.planGroup,
          level: plan.unit?.name || "Department",
          progress: plan.target && plan.achievedTarget ? 
            Math.round((plan.achievedTarget / plan.target) * 100) : 0,
          target: plan.target,
          achievedTarget: plan.achievedTarget,
          relationshipType: "cascadeFrom",
          hasChildren: plan.canUpdate === false,
          assignedTo: plan.assignedTo,
          progressStatus: plan.progressStatus,
          scale: plan.scale,
          scaleUnit: plan.scaleUnit
        }));
        
        const newCache = new Map(childrenCache);
        newCache.set(planId, transformedChildren);
        setChildrenCache(newCache);
        
        return transformedChildren;
      }
    } catch (error) {
      console.error("Error fetching cascaded plans:", error);
    }
    return [];
  };

  const fetchSplitPlans = async (planId) => {
    try {
      const apiResponse = await GetSplitPlanApi(planId, 0, 50);
      
      if (apiResponse.success && apiResponse.data?.plans) {
        const transformedChildren = apiResponse.data.plans.map(plan => ({
          id: plan.planId,
          name: plan.title,
          planGroup: plan.planGroup?.name || plan.planGroup,
          level: plan.unit?.name || "Department",
          progress: plan.target && plan.achievedTarget ? 
            Math.round((plan.achievedTarget / plan.target) * 100) : 0,
          target: plan.target,
          achievedTarget: plan.achievedTarget,
          relationshipType: "splitFrom",
          assignedTo: plan.assignedTo,
          progressStatus: plan.progressStatus,
          scale: plan.scale,
          scaleUnit: plan.scaleUnit
        }));
        
        const newCache = new Map(splitChildrenCache);
        newCache.set(planId, transformedChildren);
        setSplitChildrenCache(newCache);
        
        return transformedChildren;
      }
    } catch (error) {
      console.error("Error fetching split plans:", error);
    }
    return [];
  };

  const handlePlanClick = (planId) => {
    setSelectedPlanId(planId);
    setPlanModal(true);
  };

  const toggleNode = async (nodeId, type) => {
    const expandKey = `${nodeId}-${type}`;
    const otherType = type === 'cascade' ? 'split' : 'cascade';
    const otherExpandKey = `${nodeId}-${otherType}`;
    const newExpanded = new Set(expandedNodes);
    
    if (newExpanded.has(expandKey)) {
      // Collapse current type
      newExpanded.delete(expandKey);
    } else {
      // Expand current type and collapse other type
      newExpanded.add(expandKey);
      newExpanded.delete(otherExpandKey); // Remove other type if expanded
      
      if (type === 'cascade' && !childrenCache.has(nodeId)) {
        await fetchCascadedPlans(nodeId);
      } else if (type === 'split' && !splitChildrenCache.has(nodeId)) {
        await fetchSplitPlans(nodeId);
      }
    }
    
    setExpandedNodes(newExpanded);
  };

  const formatValue = (value, scale) => {
    if (!value) return '0';
    
    const numValue = parseFloat(value);
    
    // Use the same logic as targetHelpers.js
    if (!scale || !scale.scaleName) {
      // Fallback to basic number formatting
      return getNumberInText(numValue);
    }
    
    const scaleName = scale.scaleName;
    const scaleUnitAbbreviation = scale.scaleUnitAbbreviation || '';
    
    if (scaleName === "Number") {
      return getNumberInText(numValue);
    } else if (scaleName === "Currency") {
      return getAmountInText(numValue, scaleUnitAbbreviation);
    } else if (scaleName === "Percentage") {
      return numValue + "%";
    } else {
      return numValue + scaleUnitAbbreviation;
    }
  };

  // Helper functions from targetHelpers.js
  const getAmountInText = (amount, abb) => {
    let currencyType = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: abb || "USD",
    });
    // Nine Zeroes for Billions
    return Math.abs(Number(amount)) >= 1.0e9
      ? currencyType.format(0).split("0")[0] +
          (Math.abs(Number(amount)) / 1.0e9).toFixed(2) +
          "B"
      : // Six Zeroes for Millions
      Math.abs(Number(amount)) >= 1.0e6
      ? currencyType.format(0).split("0")[0] +
        (Math.abs(Number(amount)) / 1.0e6).toFixed(2) +
        "M"
      : currencyType.format(amount);
  };

  const getNumberInText = (amount) => {
    let type = Intl.NumberFormat("en-US");
    // Nine Zeroes for Billions
    return Math.abs(Number(amount)) >= 1.0e9
      ? (Math.abs(Number(amount)) / 1.0e9).toFixed(2) + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(amount)) >= 1.0e6
      ? (Math.abs(Number(amount)) / 1.0e6).toFixed(2) + "M"
      : type.format(amount);
  };

  const renderPlanNode = (plan, level = 0) => {
    const children = childrenCache.get(plan.id) || [];
    const splitChildren = splitChildrenCache.get(plan.id) || [];
    const isCascadeExpanded = expandedNodes.has(`${plan.id}-cascade`);
    const isSplitExpanded = expandedNodes.has(`${plan.id}-split`);

    const getNodeClass = () => {
      let baseClass = `tree-node level-${Math.min(level, 2)}`;
      
      // Add inactive styling if plan is inactive OR if any ancestor is inactive
      const isEffectivelyInactive = plan.isActive === 0 || plan.isActive === false || plan.hasInactiveAncestor === true;
      if (isEffectivelyInactive) baseClass += ' tree-node-inactive';
      
      if (level === 0) return `${baseClass} tree-node-top`;
      if (plan.relationshipType === "cascadeFrom") return `${baseClass} tree-node-cascade`;
      if (plan.relationshipType === "splitFrom") return `${baseClass} tree-node-split`;
      return `${baseClass} tree-node-default`;
    };

    return (
      <div key={plan.id} className="tree-node-container">
        <div className={getNodeClass()}>
          <div 
            className="plan-card" 
            style={{ 
              cursor: (plan.isActive === 0 || plan.isActive === false || plan.hasInactiveAncestor === true) ? 'not-allowed' : 'pointer',
              opacity: (plan.isActive === 0 || plan.isActive === false || plan.hasInactiveAncestor === true) ? 0.5 : 1
            }}
            onClick={() => {
              // Don't open modal if plan is inactive OR if any ancestor is inactive
              const isEffectivelyInactive = plan.isActive === 0 || plan.isActive === false || plan.hasInactiveAncestor === true;
              if (!isEffectivelyInactive) {
                handlePlanClick(plan.id);
              }
            }}
          >
            {/* Expansion Buttons */}
            {plan.hasChildren && (
              <button
                className="expand-btn cascade-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(plan.id, 'cascade');
                }}
              >
                {isCascadeExpanded ? <MinusOutlined /> : <PlusOutlined />}
              </button>
            )}

            {plan.hasSplitPlans && (
              <button
                className="expand-btn split-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(plan.id, 'split');
                }}
              >
                {isSplitExpanded ? <MinusOutlined /> : <PlusOutlined />}
              </button>
            )}

            <div className="plan-content">
              {/* Plan Group */}
              {plan.planGroup && (
                <div className="plan-group">{plan.planGroup}</div>
              )}

              {/* Plan Title */}
              <h3 className="plan-title">{plan.name}</h3>

              {/* Target Value */}
              {plan.target && (
                <div className="plan-target">
                  <span>Target: {formatValue(plan.target, plan.scale)}</span>
                </div>
              )}

              {/* Progress */}
              <div className="plan-progress">
                <div className="progress-info">
                  <span>Progress</span>
                  <span>{plan.progress}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${plan.progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Level */}
              <div className="plan-level">{plan.level}</div>
            </div>
          </div>
        </div>

        {/* Children Container */}
        {((isCascadeExpanded && children.length > 0) || (isSplitExpanded && splitChildren.length > 0)) && (
          <div className="tree-children">
            {/* Cascade Children */}
            {isCascadeExpanded && children.length > 0 && (
              <div className="cascade-children">
                {children.map(child => renderPlanNode(child, level + 1))}
              </div>
            )}

            {/* Split Children */}
            {isSplitExpanded && splitChildren.length > 0 && (
              <div className="split-children">
                {splitChildren.map(child => renderPlanNode(child, level + 1))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!treeData || !treeData.plans || treeData.plans.length === 0) {
    return (
      <div className="tree-empty-state">
        <WarningOutlined className="empty-icon" />
        <div className="empty-title">No Plans Available</div>
        <div className="empty-description">No plans found for this performance cycle</div>
      </div>
    );
  }

  return (
    <div className="full-tree-view">
      {/* Header */}
      <div className="tree-header">
        <div className="cycle-info">
          <ApartmentOutlined className="cycle-icon" />
          <div>
            <h1 className="cycle-title">{perfCycleState.name}</h1>
          </div>
        </div>
      </div>

      {/* Plans Count */}
      <div className="plans-count">
        <NodeIndexOutlined /> Top Level Plans ({treeData.plans.length})
      </div>

      {/* Tree Container */}
      <div className="tree-container">
        {treeData.plans.map(plan => renderPlanNode(plan, 0))}
      </div>

      <PlanInfoModal
        visible={planModal}
        handleCancel={() => {
          setPlanModal(false);
          setSelectedPlanId("");
        }}
        planId={selectedPlanId}
      />
    </div>
  );
};

export default FullTreeViewMock;
